import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Lock, Heart, Users, Mail } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const PrivacyPolicyPage = () => {

  const privacyPromises = [
    { icon: Shield, text: 'Your data is protected with care and respect' },
    { icon: Lock, text: 'We do not sell personal information to advertisers' },
    { icon: Heart, text: 'Trust is central to everything we build' },
    { icon: Users, text: 'Family data is handled with extra thoughtfulness' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFCFA]" data-testid="privacy-policy-page">
      <Navigation />

      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            
            {/* Header Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-10 md:mb-14"
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-1.5 text-sm text-[#7A7A7A] hover:text-[#1A1A1A] transition-colors mb-8"
              >
                <span>←</span>
                <span>Back to Home</span>
              </Link>
              
              <div className="max-w-2xl">
                <p className="text-sm uppercase tracking-widest text-[#8A8A8A] mb-3">Legal</p>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4 leading-tight">
                  Privacy Policy
                </h1>
                <p className="text-[#7A7A7A] mb-6">Last Updated: March 16, 2026</p>
                <p className="text-lg text-[#4A4A4A] leading-relaxed">
                  Connect Band is committed to protecting your privacy with care and respect. This policy explains how we collect, use, and protect your information.
                </p>
              </div>
            </motion.div>

            {/* Privacy Promise Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-16 md:mb-20"
            >
              <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-3xl border border-black/5 p-8 md:p-10 shadow-sm">
                <h2 className="text-lg font-semibold text-[#1A1A1A] mb-6">Our Privacy Promise</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {privacyPromises.map((promise, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#F5F0EB] flex items-center justify-center flex-shrink-0">
                        <promise.icon className="w-4 h-4 text-[#6A6A6A]" />
                      </div>
                      <p className="text-[#4A4A4A] text-sm leading-relaxed pt-1.5">{promise.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="space-y-14 md:space-y-18">
              
              {/* Information We Collect */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Information We Collect
                </h2>
                
                <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-[#8A8A8A] mb-4">Information You Provide</h3>
                    <ul className="space-y-2.5 text-[#4A4A4A]">
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Name and email address</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Account and profile details</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Preferences and settings</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Messages and support requests</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Early access reservations</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-[#8A8A8A] mb-4">From Connect Products</h3>
                    <ul className="space-y-2.5 text-[#4A4A4A]">
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Activity and movement data</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Sleep and wellness insights</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>Device connection details</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA] mt-2 flex-shrink-0" />
                        <span>App usage information</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-[#FAF8F5] rounded-2xl">
                  <h3 className="text-sm uppercase tracking-wider text-[#8A8A8A] mb-3">Automatically Collected</h3>
                  <p className="text-[#4A4A4A] text-sm leading-relaxed">
                    Technical information such as browser type, device type, IP address, and general usage patterns through cookies and similar technologies.
                  </p>
                </div>
              </motion.section>

              {/* How We Use Information */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  How We Use Information
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    'Provide and operate our Services',
                    'Create and manage your account',
                    'Deliver app and device features',
                    'Improve product experience',
                    'Respond to support inquiries',
                    'Communicate important updates',
                    'Protect safety and security',
                    'Comply with legal obligations',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#F0EBE5] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs text-[#8A8A8A]">{index + 1}</span>
                      </div>
                      <p className="text-[#4A4A4A] text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* How We Share Information */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  How We Share Information
                </h2>
                <p className="text-[#4A4A4A] leading-relaxed mb-8 max-w-2xl">
                  We share information only in limited situations and never sell personal information to advertisers or data brokers.
                </p>
                <div className="space-y-6">
                  {[
                    { title: 'Within Shared Experiences', desc: 'When you use Connect Family or Connect Care, certain information may be visible to other authorized participants.' },
                    { title: 'Service Providers', desc: 'Trusted providers supporting hosting, payments, analytics, and customer support, bound by appropriate obligations.' },
                    { title: 'Legal Requirements', desc: 'When required by law or to protect rights, safety, users, or the integrity of our Services.' },
                    { title: 'Business Changes', desc: 'In the event of a merger, acquisition, or sale of assets, subject to applicable law.' },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 p-5 bg-white rounded-2xl border border-black/5">
                      <div className="w-8 h-8 rounded-xl bg-[#F5F0EB] flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-[#6A6A6A]">{index + 1}</span>
                      </div>
                      <div>
                        <h3 className="font-medium text-[#1A1A1A] mb-1">{item.title}</h3>
                        <p className="text-[#5A5A5A] text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* Data Security & Retention */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">Data Security</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      We take reasonable administrative, technical, and organizational measures to protect your personal information. While no system can guarantee absolute security, we maintain safeguards appropriate to the nature of the information we handle.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">Data Retention</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      We keep personal information for as long as reasonably necessary to provide Services, maintain accounts, support users, comply with legal obligations, and resolve disputes.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Your Rights */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Your Rights & Choices
                </h2>
                <p className="text-[#4A4A4A] leading-relaxed mb-6 max-w-2xl">
                  Depending on your location, you may have rights to access, correct, or delete your information, and to manage communication preferences.
                </p>
                <div className="inline-flex items-center gap-2 px-5 py-3 bg-[#1A1A1A] text-white rounded-full text-sm">
                  <Mail className="w-4 h-4" />
                  <span>Contact support@connectband.health to make a request</span>
                </div>
              </motion.section>

              {/* Family & Children */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Family Accounts</h2>
                    <p className="text-[#4A4A4A] leading-relaxed mb-4">
                      Connect may offer shared experiences for families. Participation should be based on appropriate consent and account permissions.
                    </p>
                    <p className="text-[#5A5A5A] text-sm">
                      We encourage families to make thoughtful choices about what they share and with whom.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Children's Privacy</h2>
                    <p className="text-[#4A4A4A] leading-relaxed mb-4">
                      Where minors are involved, parents or guardians are responsible for account decisions, permissions, and supervision.
                    </p>
                    <p className="text-[#5A5A5A] text-sm">
                      We do not intend to collect information from children inconsistent with applicable law.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Updates & Third Parties */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Third-Party Services</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      Our Services may link to third-party products. Their privacy practices are governed by their own policies. We encourage you to review them.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Policy Updates</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      We may update this policy from time to time. Material changes will be communicated through appropriate notice. Continued use means acceptance.
                    </p>
                  </div>
                </div>
              </motion.section>

            </div>

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-20 md:mt-24"
            >
              <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] rounded-3xl p-8 md:p-12 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3">
                  Questions about privacy?
                </h2>
                <p className="text-white/70 mb-8 max-w-md mx-auto">
                  Reach out to our team anytime. We're here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="mailto:hello@connectband.health"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#1A1A1A] rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    hello@connectband.health
                  </a>
                  <a 
                    href="mailto:support@connectband.health"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    support@connectband.health
                  </a>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
